/**
 * Vela sort animation (Rules empty-state visual).
 *
 * SVG + CSS animation showing three transaction rows getting
 * tagged with category labels as the Vela wand moves between
 * them. Metaphor: Rules auto-categorize your transactions.
 *
 * Infinite 4-second loop. Pure presentational, no props-driven
 * state. CSS keyframes run on the GPU so the animation is cheap.
 *
 * Reused by:
 *   - RulesEmptyState (Bank rules tab, Integration rules tab)
 *   - Potentially the Rule builder drawer intro screen (follow-up)
 *
 * Why its own component: this animation is Rules-specific and
 * must not be confused with the Accounting Overview book
 * animation. Keeping it in src/components (not src/pages/Accounting/Rules)
 * so other Vela-driven features can reuse it later (Vela
 * suggestions banner, Vela inbox hero, etc.).
 */

import type { CSSProperties } from "react";

interface VelaSortAnimationProps {
  /** Pixel width (height scales proportionally). Default 180. */
  width?: number;
  /**
   * Three short uppercase category labels shown on the tags as
   * each row gets categorized. Defaults vary by context:
   *   Bank rules empty state:        ["OFFICE", "SALES", "TRAVEL"]
   *   Integration rules empty state: ["ONLINE", "PAYOUT", "TIPS"]
   */
  tagLabels?: [string, string, string];
}

const DEFAULT_TAG_LABELS: [string, string, string] = ["OFFICE", "SALES", "TRAVEL"];

const PALETTE = {
  ink: "#0E1A1A",
  tealDark: "#0F6E56",
  vela: "#6B46C1",
  line: "#D8DEE6",
  bgFade: "#F3F5F5",
};

const ASPECT_RATIO = 134 / 180; // Height = width * ratio

export function VelaSortAnimation({
  width = 180,
  tagLabels = DEFAULT_TAG_LABELS,
}: VelaSortAnimationProps) {
  const height = Math.round(width * ASPECT_RATIO);

  const wrapStyle: CSSProperties = {
    width,
    height,
    display: "block",
    margin: "0 auto",
    position: "relative",
  };

  return (
    <div style={wrapStyle} aria-hidden="true">
      <style>{ANIMATION_CSS}</style>
      <svg
        viewBox="0 0 180 134"
        width={width}
        height={height}
        style={{ display: "block", overflow: "visible" }}
      >
        {/* Transaction row 1 */}
        <g>
          <rect className="vsa-txn-bg" x="10" y="18" width="100" height="20" rx="4" />
          <circle className="vsa-txn-icon" cx="22" cy="28" r="4" />
          <rect className="vsa-txn-line" x="34" y="24" width="60" height="4" rx="2" />
          <rect className="vsa-txn-sub" x="34" y="31" width="38" height="3" rx="1.5" />
        </g>

        {/* Transaction row 2 */}
        <g>
          <rect className="vsa-txn-bg" x="10" y="56" width="100" height="20" rx="4" />
          <circle className="vsa-txn-icon" cx="22" cy="66" r="4" />
          <rect className="vsa-txn-line" x="34" y="62" width="60" height="4" rx="2" />
          <rect className="vsa-txn-sub" x="34" y="69" width="42" height="3" rx="1.5" />
        </g>

        {/* Transaction row 3 */}
        <g>
          <rect className="vsa-txn-bg" x="10" y="94" width="100" height="20" rx="4" />
          <circle className="vsa-txn-icon" cx="22" cy="104" r="4" />
          <rect className="vsa-txn-line" x="34" y="100" width="60" height="4" rx="2" />
          <rect className="vsa-txn-sub" x="34" y="107" width="36" height="3" rx="1.5" />
        </g>

        {/* Category tag 1 (teal) */}
        <g className="vsa-tag vsa-tag-1">
          <rect x="118" y="22" width="54" height="12" rx="6" fill={PALETTE.tealDark} />
          <text className="vsa-tag-text" x="145" y="30.5" textAnchor="middle">
            {tagLabels[0]}
          </text>
        </g>

        {/* Category tag 2 (ink) */}
        <g className="vsa-tag vsa-tag-2">
          <rect x="118" y="60" width="54" height="12" rx="6" fill={PALETTE.ink} />
          <text className="vsa-tag-text" x="145" y="68.5" textAnchor="middle">
            {tagLabels[1]}
          </text>
        </g>

        {/* Category tag 3 (vela) */}
        <g className="vsa-tag vsa-tag-3">
          <rect x="118" y="98" width="54" height="12" rx="6" fill={PALETTE.vela} />
          <text className="vsa-tag-text" x="145" y="106.5" textAnchor="middle">
            {tagLabels[2]}
          </text>
        </g>

        {/* Vela wand (travels between rows) */}
        <g className="vsa-wand">
          <circle className="vsa-wand-ring" r="9" fill="none" stroke={PALETTE.vela} strokeWidth="1.5" />
          <circle r="9" fill={PALETTE.vela} />
          <text y="3.5" textAnchor="middle" fill="#ffffff" fontSize="10" fontWeight="700">
            V
          </text>
        </g>
      </svg>
    </div>
  );
}

/**
 * Scoped CSS. Inlined via <style> inside the component so the
 * animation does not require importing a .css file or touching
 * the global stylesheet. Class names are prefixed `vsa-` to avoid
 * collision with other animations.
 */
const ANIMATION_CSS = `
  .vsa-txn-bg { fill: ${PALETTE.bgFade}; stroke: ${PALETTE.line}; stroke-width: 1; }
  .vsa-txn-icon { fill: ${PALETTE.line}; }
  .vsa-txn-line { fill: ${PALETTE.line}; }
  .vsa-txn-sub { fill: ${PALETTE.line}; opacity: 0.6; }

  .vsa-tag { opacity: 0; transform-origin: left center; transform-box: fill-box; }
  .vsa-tag-text {
    fill: #ffffff;
    font-size: 7px;
    font-weight: 700;
    font-family: -apple-system, "Segoe UI", Roboto, sans-serif;
    letter-spacing: 0.03em;
  }

  @keyframes vsa-cat-1 {
    0%, 12% { opacity: 0; transform: translateX(8px); }
    20%, 90% { opacity: 1; transform: translateX(0); }
    100% { opacity: 0; transform: translateX(8px); }
  }
  @keyframes vsa-cat-2 {
    0%, 32% { opacity: 0; transform: translateX(8px); }
    40%, 90% { opacity: 1; transform: translateX(0); }
    100% { opacity: 0; transform: translateX(8px); }
  }
  @keyframes vsa-cat-3 {
    0%, 52% { opacity: 0; transform: translateX(8px); }
    60%, 90% { opacity: 1; transform: translateX(0); }
    100% { opacity: 0; transform: translateX(8px); }
  }
  .vsa-tag-1 { animation: vsa-cat-1 4s ease-in-out infinite; }
  .vsa-tag-2 { animation: vsa-cat-2 4s ease-in-out infinite; }
  .vsa-tag-3 { animation: vsa-cat-3 4s ease-in-out infinite; }

  @keyframes vsa-wand-move {
    0%, 10% { transform: translate(130px, 28px); opacity: 1; }
    22%, 32% { transform: translate(130px, 66px); opacity: 1; }
    45%, 60% { transform: translate(130px, 104px); opacity: 1; }
    72% { transform: translate(130px, 104px); opacity: 0; }
    88% { transform: translate(130px, 28px); opacity: 0; }
    98%, 100% { transform: translate(130px, 28px); opacity: 1; }
  }
  @keyframes vsa-wand-ring {
    0% { transform: scale(1); opacity: 0.55; }
    80%, 100% { transform: scale(2.2); opacity: 0; }
  }
  .vsa-wand { animation: vsa-wand-move 4s ease-in-out infinite; }
  .vsa-wand-ring {
    transform-origin: center;
    transform-box: fill-box;
    animation: vsa-wand-ring 1.1s ease-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .vsa-tag-1,
    .vsa-tag-2,
    .vsa-tag-3,
    .vsa-wand,
    .vsa-wand-ring {
      animation: none;
    }
    .vsa-tag { opacity: 1; }
    .vsa-wand { transform: translate(130px, 66px); opacity: 1; }
  }
`;